import { Badge } from "@/core/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/core/components/ui/card";
import { NoTranslate } from "@/core/components/ui/no-translate";
import { formatDateWithFallback } from "@/core/utils/func/formatDateWithFallback";
import { AmountMinor } from "@/features/shared";
import type { TransactionDto } from "@/features/wallet/api/dto";
import { useTransactionsTranslation } from "@/features/wallet/i18n/use-transactions-translation";
import { TransactionStatusBadge } from "./transaction-status-badge";

/**
 * Reçu d'une transaction (`GET /Transactions/{id}`) : montant, frais, total,
 * statuts et références — données financières en `NoTranslate` (règle
 * notranslate). Le total débité suppose le payeur de frais SENDER (défaut
 * serveur) : principal + frais.
 */
export function TransactionDetailCard({
  transaction,
}: {
  transaction: TransactionDto;
}) {
  const { t } = useTransactionsTranslation();

  const totalMinor = transaction.amountMinor + transaction.feeAmountMinor;

  const fields: { label: string; value?: string | null }[] = [
    { label: t("wallet.transactions.form.type"), value: transaction.type },
    {
      label: t("wallet.transactions.form.productCode"),
      value: transaction.productCode,
    },
    {
      label: t("wallet.transactions.columns.sourceWalletId"),
      value: transaction.sourceWalletId,
    },
    {
      label: t("wallet.transactions.columns.destination"),
      value:
        transaction.destinationWalletId ?? transaction.destinationExternalRef,
    },
    {
      label: t("wallet.transactions.detail.feePayer"),
      value: transaction.feePayer,
    },
    {
      label: t("wallet.transactions.detail.currency"),
      value: transaction.currency,
    },
    {
      label: t("wallet.transactions.columns.initiatedAt"),
      value: transaction.initiatedAt
        ? formatDateWithFallback(transaction.initiatedAt, "—")
        : null,
    },
    {
      label: t("wallet.transactions.detail.completedAt"),
      value: transaction.completedAt
        ? formatDateWithFallback(transaction.completedAt, "—")
        : null,
    },
    {
      label: t("wallet.transactions.detail.failureReason"),
      value: transaction.failureReason,
    },
    {
      label: t("wallet.transactions.detail.externalProviderReference"),
      value: transaction.externalProviderReference,
    },
    {
      label: t("wallet.transactions.form.customerReference"),
      value: transaction.customerReference,
    },
    {
      label: t("wallet.transactions.detail.agentId"),
      value: transaction.agentId,
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2 text-base">
          <span>{t("wallet.transactions.detail.receipt")}</span>
          <NoTranslate as="span">
            <Badge variant="secondary">{transaction.reference}</Badge>
          </NoTranslate>
          <TransactionStatusBadge status={transaction.status} />
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <dt className="text-sm text-muted-foreground">
              {t("wallet.transactions.detail.amount")}
            </dt>
            <dd>
              <AmountMinor
                amountMinor={transaction.amountMinor}
                currency={transaction.currency}
                className="text-lg font-semibold"
              />
            </dd>
          </div>
          <div>
            <dt className="text-sm text-muted-foreground">
              {t("wallet.transactions.detail.fee")}
            </dt>
            <dd>
              <AmountMinor
                amountMinor={transaction.feeAmountMinor}
                currency={transaction.currency}
                className="text-lg font-semibold"
              />
            </dd>
          </div>
          <div>
            <dt className="text-sm text-muted-foreground">
              {t("wallet.transactions.detail.total")}
            </dt>
            <dd>
              <AmountMinor
                amountMinor={totalMinor}
                currency={transaction.currency}
                className="text-lg font-semibold text-accent-dark dark:text-accent-light"
              />
            </dd>
          </div>
        </dl>

        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {fields.map(({ label, value }) => (
            <div key={label}>
              <dt className="text-sm text-muted-foreground">{label}</dt>
              <dd className="font-medium">
                <NoTranslate as="span">{value || "—"}</NoTranslate>
              </dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}
